// Web 应用存储适配器（使用 localStorage）
import type { AppData, Workspace, Component, SectionComponent, ContentItem, BookmarkContentItem, BaseComponent } from '@ext-bookmark/shared/types';
import { generateId } from './utils';

const STORAGE_KEY = 'bookmark_navigator_data';

// 默认数据
const defaultData: AppData = {
  workspaces: [],
  theme: 'light',
  todos: [],
};

/**
 * 获取所有数据
 */
export async function getAppData(): Promise<AppData> {
  try {
    const dataStr = localStorage.getItem(STORAGE_KEY);
    if (!dataStr) {
      return defaultData;
    }
    const data = JSON.parse(dataStr);
    return data || defaultData;
  } catch (error) {
    console.warn('Failed to get app data:', error);
    return defaultData;
  }
}

/**
 * 保存所有数据
 */
export async function saveAppData(data: AppData, skipAutoSync: boolean = false): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    
    // 触发自动同步（异步，不阻塞）
    if (!skipAutoSync) {
      // 延迟加载自动同步服务，避免循环依赖
      import('../services/syncService').then(({ syncService }) => {
        syncService.enqueueSync(data, 'UPDATE').catch((error) => {
          console.debug('[Storage] 自动同步失败（可能未登录或未启用）:', error);
        });
      }).catch(() => {
        // 如果 syncService 不存在，忽略错误
      });
    }
  } catch (error) {
    console.warn('Failed to save app data:', error);
  }
}

// ==================== 工作区操作 ====================

export async function getAllWorkspaces(): Promise<Workspace[]> {
  const data = await getAppData();
  return data.workspaces;
}

export async function getWorkspace(id: string): Promise<Workspace | null> {
  const data = await getAppData();
  return data.workspaces.find(w => w.id === id) || null;
}

export async function addWorkspace(workspace: Omit<Workspace, 'id' | 'createdAt' | 'updatedAt'>): Promise<Workspace> {
  const data = await getAppData();
  const newWorkspace: Workspace = {
    ...workspace,
    id: generateId(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
    components: workspace.components || [],
  };
  data.workspaces.push(newWorkspace);
  await saveAppData(data);
  return newWorkspace;
}

export async function updateWorkspace(id: string, updates: Partial<Workspace>): Promise<Workspace | null> {
  const data = await getAppData();
  const index = data.workspaces.findIndex(w => w.id === id);
  if (index === -1) return null;
  
  data.workspaces[index] = {
    ...data.workspaces[index],
    ...updates,
    updatedAt: Date.now(),
  };
  await saveAppData(data);
  return data.workspaces[index];
}

export async function deleteWorkspace(id: string): Promise<boolean> {
  const data = await getAppData();
  const index = data.workspaces.findIndex(w => w.id === id);
  if (index === -1) return false;
  
  data.workspaces.splice(index, 1);
  if (data.currentWorkspaceId === id) {
    data.currentWorkspaceId = data.workspaces[0]?.id;
  }
  await saveAppData(data);
  return true;
}

export async function setCurrentWorkspace(workspaceId: string | undefined): Promise<void> {
  const data = await getAppData();
  data.currentWorkspaceId = workspaceId;
  await saveAppData(data);
}

export async function getCurrentWorkspace(): Promise<Workspace | null> {
  const data = await getAppData();
  if (!data.currentWorkspaceId) return null;
  return getWorkspace(data.currentWorkspaceId);
}

// ==================== 组件操作 ====================

export async function addComponent(
  workspaceId: string,
  component: Omit<BaseComponent, 'id' | 'createdAt' | 'updatedAt' | 'position'>
): Promise<Component | null> {
  const data = await getAppData();
  const workspace = data.workspaces.find(w => w.id === workspaceId);
  if (!workspace) return null;

  if (!workspace.components) {
    workspace.components = [];
  }

  const siblings = workspace.components.filter(c => c.parentId === component.parentId);
  const maxPosition = siblings.length > 0
    ? Math.max(...siblings.map(c => c.position))
    : -1;

  let newComponent: Component;
  if (component.componentType === 'section') {
    const sectionConfig = {
      x: 50,
      y: 50,
      width: 300,
      height: 400,
      locked: false,
      ...component.config,
    };
    
    newComponent = {
      ...component,
      id: generateId(),
      position: maxPosition + 1,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      config: sectionConfig,
      contentItems: [],
    } as SectionComponent;
  } else {
    return null;
  }

  workspace.components.push(newComponent);
  workspace.updatedAt = Date.now();
  await saveAppData(data);
  return newComponent;
}

export async function updateComponent(
  workspaceId: string,
  componentId: string,
  updates: Partial<Component>
): Promise<Component | null> {
  const data = await getAppData();
  const workspace = data.workspaces.find(w => w.id === workspaceId);
  if (!workspace || !workspace.components) return null;

  const index = workspace.components.findIndex(c => c.id === componentId);
  if (index === -1) return null;

  const currentComponent = workspace.components[index];
  
  let mergedUpdates: Partial<Component> = { ...updates };
  if (updates.config && currentComponent.componentType === 'section' && 'config' in currentComponent) {
    mergedUpdates = {
      ...updates,
      config: {
        ...(currentComponent as any).config,
        ...updates.config,
      },
    };
  }

  workspace.components[index] = {
    ...currentComponent,
    ...mergedUpdates,
    updatedAt: Date.now(),
  } as Component;

  workspace.updatedAt = Date.now();
  await saveAppData(data);
  return workspace.components[index];
}

export async function deleteComponent(
  workspaceId: string,
  componentId: string
): Promise<boolean> {
  const data = await getAppData();
  const workspace = data.workspaces.find(w => w.id === workspaceId);
  if (!workspace || !workspace.components) return false;

  const index = workspace.components.findIndex(c => c.id === componentId);
  if (index === -1) return false;

  const _component = workspace.components[index];
  void _component;
  const childComponents = workspace.components.filter(c => c.parentId === componentId);
  
  for (const child of childComponents) {
    await deleteComponent(workspaceId, child.id);
  }

  workspace.components.splice(index, 1);
  workspace.updatedAt = Date.now();
  await saveAppData(data);
  return true;
}

export async function getComponent(
  workspaceId: string,
  componentId: string
): Promise<Component | null> {
  const data = await getAppData();
  const workspace = data.workspaces.find(w => w.id === workspaceId);
  if (!workspace || !workspace.components) return null;

  return workspace.components.find(c => c.id === componentId) || null;
}

export async function getChildComponents(
  workspaceId: string,
  parentId?: string
): Promise<Component[]> {
  const data = await getAppData();
  const workspace = data.workspaces.find(w => w.id === workspaceId);
  if (!workspace || !workspace.components) return [];

  return workspace.components
    .filter(c => c.parentId === parentId)
    .sort((a, b) => a.position - b.position);
}

// ==================== 内容项操作 ====================

export async function addContentItemToComponent(
  workspaceId: string,
  componentId: string,
  contentItem: Omit<ContentItem, 'id' | 'position'>
): Promise<ContentItem | null> {
  const component = await getComponent(workspaceId, componentId);
  if (!component || component.componentType !== 'section') return null;
  
  const sectionComponent = component as SectionComponent;
  const maxPosition = sectionComponent.contentItems.length > 0
    ? Math.max(...sectionComponent.contentItems.map(item => item.position))
    : -1;
  
  let newContentItem: ContentItem;
  if (contentItem.type === 'bookmark') {
    const bookmarkItem = contentItem as Omit<BookmarkContentItem, 'id' | 'position'>;
    newContentItem = {
      ...bookmarkItem,
      id: generateId(),
      position: maxPosition + 1,
      bookmark: {
        ...bookmarkItem.bookmark,
        id: bookmarkItem.bookmark.id || generateId(),
      },
    } as BookmarkContentItem;
  } else {
    newContentItem = {
      ...contentItem,
      id: generateId(),
      position: maxPosition + 1,
    } as ContentItem;
  }
  
  const updatedContentItems = [...sectionComponent.contentItems, newContentItem];
  await updateComponent(workspaceId, componentId, {
    contentItems: updatedContentItems,
  } as Partial<SectionComponent>);
  
  return newContentItem;
}

export async function updateContentItemInComponent(
  workspaceId: string,
  componentId: string,
  contentItemId: string,
  updates: Partial<ContentItem>
): Promise<ContentItem | null> {
  const component = await getComponent(workspaceId, componentId);
  if (!component || component.componentType !== 'section') return null;
  
  const sectionComponent = component as SectionComponent;
  const index = sectionComponent.contentItems.findIndex(item => item.id === contentItemId);
  if (index === -1) return null;
  
  const updatedContentItems = [...sectionComponent.contentItems];
  updatedContentItems[index] = {
    ...updatedContentItems[index],
    ...updates,
  } as ContentItem;
  
  await updateComponent(workspaceId, componentId, {
    contentItems: updatedContentItems,
  } as Partial<SectionComponent>);
  
  return updatedContentItems[index];
}

export async function deleteContentItemFromComponent(
  workspaceId: string,
  componentId: string,
  contentItemId: string
): Promise<boolean> {
  const component = await getComponent(workspaceId, componentId);
  if (!component || component.componentType !== 'section') return false;
  
  const sectionComponent = component as SectionComponent;
  const index = sectionComponent.contentItems.findIndex(item => item.id === contentItemId);
  if (index === -1) return false;
  
  const updatedContentItems = sectionComponent.contentItems.filter(item => item.id !== contentItemId);
  await updateComponent(workspaceId, componentId, {
    contentItems: updatedContentItems,
  } as Partial<SectionComponent>);
  
  return true;
}
